import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Text,
  Box,
  VStack,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import * as Yup from "yup";
import { IntakeFormAnswers } from "~/../../sdk/consultations";
import { SelectInput } from "~/components";
import { Checkbox } from "~/OnboardingApp/components";

export interface WomensFertilityFormValues {
  trying_to_conceive: string;
  last_menstrual_period: string;
  regular_periods: string;
  cycle_length: string;
  lab_test_media_ids: string[];
  consent: boolean;
}

interface WomensFertilityIntakeFormProps {
  labTestMediaIds: string[];
}

interface WomensFertilityIntakeFormComponent
  extends FC<WomensFertilityIntakeFormProps> {
  getInitialValues: () => WomensFertilityFormValues;
  getValidationSchema: (t: any) => any;
  buildIntakeAnswers: (
    values: WomensFertilityFormValues,
    labTestMediaIds: string[],
  ) => IntakeFormAnswers;
}

export const WomensFertilityIntakeForm: WomensFertilityIntakeFormComponent =
  () => {
    const { t } = useTranslation();
    const formik = useFormikContext<WomensFertilityFormValues>();

    return (
      <VStack w="full" alignItems="flex-start" spacing={6}>
        {/* Question 1: How long trying to conceive */}
        <SelectInput
          id="trying_to_conceive"
          label={t(
            "diagnosticDashboard.intake.questions.tryingToConceive.label",
          )}
          placeholder={t(
            "diagnosticDashboard.intake.questions.tryingToConceive.placeholder",
          )}
          options={[
            {
              value: t(
                "diagnosticDashboard.intake.questions.tryingToConceive.options.notYetSoon",
              ),
              label: t(
                "diagnosticDashboard.intake.questions.tryingToConceive.options.notYetSoon",
              ),
            },
            {
              value: t(
                "diagnosticDashboard.intake.questions.tryingToConceive.options.notYetNotSoon",
              ),
              label: t(
                "diagnosticDashboard.intake.questions.tryingToConceive.options.notYetNotSoon",
              ),
            },
            {
              value: t(
                "diagnosticDashboard.intake.questions.tryingToConceive.options.oneToSix",
              ),
              label: t(
                "diagnosticDashboard.intake.questions.tryingToConceive.options.oneToSix",
              ),
            },
            {
              value: t(
                "diagnosticDashboard.intake.questions.tryingToConceive.options.sixToTwelve",
              ),
              label: t(
                "diagnosticDashboard.intake.questions.tryingToConceive.options.sixToTwelve",
              ),
            },
            {
              value: t(
                "diagnosticDashboard.intake.questions.tryingToConceive.options.moreThanTwelve",
              ),
              label: t(
                "diagnosticDashboard.intake.questions.tryingToConceive.options.moreThanTwelve",
              ),
            },
          ]}
        />

        {/* Question 2: Last menstrual period */}
        <FormControl
          isInvalid={
            !!(
              formik.errors.last_menstrual_period &&
              formik.touched.last_menstrual_period
            )
          }
        >
          <FormLabel htmlFor="last_menstrual_period">
            {t(
              "diagnosticDashboard.intake.questions.lastMenstrualPeriod.label",
            )}{" "}
            {t(
              "diagnosticDashboard.intake.questions.lastMenstrualPeriod.helpText",
            )}
          </FormLabel>
          <Input
            id="last_menstrual_period"
            type="date"
            {...formik.getFieldProps("last_menstrual_period")}
          />
          <FormErrorMessage>
            {formik.errors.last_menstrual_period}
          </FormErrorMessage>
        </FormControl>

        {/* Question 3: Regular periods */}
        <SelectInput
          id="regular_periods"
          label={t("diagnosticDashboard.intake.questions.regularPeriods.label")}
          placeholder={t(
            "diagnosticDashboard.intake.questions.regularPeriods.placeholder",
          )}
          options={[
            {
              value: t(
                "diagnosticDashboard.intake.questions.regularPeriods.options.regular",
              ),
              label: t(
                "diagnosticDashboard.intake.questions.regularPeriods.options.regular",
              ),
            },
            {
              value: t(
                "diagnosticDashboard.intake.questions.regularPeriods.options.mildlyIrregular",
              ),
              label: t(
                "diagnosticDashboard.intake.questions.regularPeriods.options.mildlyIrregular",
              ),
            },
            {
              value: t(
                "diagnosticDashboard.intake.questions.regularPeriods.options.irregular",
              ),
              label: t(
                "diagnosticDashboard.intake.questions.regularPeriods.options.irregular",
              ),
            },
          ]}
        />

        {/* Question 4: Cycle length */}
        <FormControl
          isInvalid={
            !!(formik.errors.cycle_length && formik.touched.cycle_length)
          }
        >
          <FormLabel htmlFor="cycle_length">
            {t("diagnosticDashboard.intake.questions.cycleLength.label")}{" "}
            {t("diagnosticDashboard.intake.questions.cycleLength.helpText")}
          </FormLabel>
          <Input
            id="cycle_length"
            type="text"
            placeholder={t(
              "diagnosticDashboard.intake.questions.cycleLength.placeholder",
            )}
            {...formik.getFieldProps("cycle_length")}
          />
          <FormErrorMessage>{formik.errors.cycle_length}</FormErrorMessage>
        </FormControl>

        {/* Question 5: Consent */}
        <Box width="100%">
          <Text fontWeight="500" mb={2}>
            {t("diagnosticDashboard.intake.questions.consent.label")}
          </Text>
          <Box p={4} bg="gray.50" borderRadius="md" mb={3} fontSize="sm">
            <Text mb={2}>
              {t("diagnosticDashboard.intake.questions.consent.disclaimer")}
            </Text>
            <Text>
              {t("diagnosticDashboard.intake.questions.consent.terms")}
            </Text>
          </Box>
          <Checkbox id="consent" name="consent">
            <Text fontSize="sm">
              {t("diagnosticDashboard.intake.questions.consent.checkbox")}
            </Text>
          </Checkbox>
        </Box>
      </VStack>
    );
  };

WomensFertilityIntakeForm.getInitialValues = (): WomensFertilityFormValues => ({
  trying_to_conceive: "",
  last_menstrual_period: "",
  regular_periods: "",
  cycle_length: "",
  lab_test_media_ids: [],
  consent: false,
});

WomensFertilityIntakeForm.getValidationSchema = (t: any) =>
  Yup.object({
    trying_to_conceive: Yup.string().required(
      t("diagnosticDashboard.intake.validation.tryingToConceiveRequired"),
    ),
    last_menstrual_period: Yup.string().required(
      t("diagnosticDashboard.intake.validation.lastMenstrualPeriodRequired"),
    ),
    regular_periods: Yup.string().required(
      t("diagnosticDashboard.intake.validation.regularPeriodsRequired"),
    ),
    cycle_length: Yup.string().required(
      t("diagnosticDashboard.intake.validation.cycleLengthRequired"),
    ),
    consent: Yup.boolean().oneOf(
      [true],
      t("diagnosticDashboard.intake.validation.consentRequired"),
    ),
  });

WomensFertilityIntakeForm.buildIntakeAnswers = (
  values: WomensFertilityFormValues,
  labTestMediaIds: string[],
): IntakeFormAnswers => ({
  trying_to_conceive: {
    fh_id: "ques_evaluation_only_fertility_intake_01",
    value: values.trying_to_conceive,
    stepId: "intake",
    type: "text" as const,
  },
  last_menstrual_period: {
    fh_id: "ques_evaluation_only_fertility_intake_02",
    value: values.last_menstrual_period,
    stepId: "intake",
    type: "text" as const,
  },
  regular_periods: {
    fh_id: "ques_evaluation_only_fertility_intake_03",
    value: values.regular_periods,
    stepId: "intake",
    type: "text" as const,
  },
  cycle_length: {
    fh_id: "ques_evaluation_only_fertility_intake_04",
    value: values.cycle_length,
    stepId: "intake",
    type: "text" as const,
  },
  consent: {
    fh_id: "ques_evaluation_only_fertility_intake_05",
    value: values.consent
      ? "I have read the above information and I do consent and wish to move forward"
      : "",
    stepId: "intake",
    type: "text" as const,
  },
  lab_test_media_ids: {
    fh_id: "ques_evaluation_only_fertility_intake_06",
    value: labTestMediaIds,
    stepId: "intake",
    type: "media" as const,
  },
});
